import type { UnitValue } from "./value.model";

/**
 * A catalogued unit: the stored pair plus the words for it.
 *
 * `name` is NOT stored on a value. It exists so a picker can say which unit
 * `in` or `deg` is, and a symbol alone cannot — UCUM is case-sensitive and
 * several symbols are near-homographs, so a list offering bare `s` beside
 * bare `S` is a mis-click away from siemens. Storage stays `{code, display}`:
 * a third redundant field on every measurement is a third thing to disagree.
 */
export interface CatalogUnit extends UnitValue {
  name: string;
}

/**
 * Every unit an author can choose, and the ONLY source of one.
 *
 * A unit is picked from this list, never typed. A free-text box — let alone one
 * asking for a UCUM code — asks an author to know UCUM's bracketing rules, and
 * the first thing that goes wrong is `in` stored where `[in_i]` was meant,
 * which is the exact failure a coded unit exists to prevent. A closed list is
 * also what makes `mmHg` one unit rather than `mmHg`, `mm Hg` and `MMHG`.
 *
 * `code` is unique across the list — it is the identity two quantities are
 * compared on, so two entries sharing one would make the display unresolvable.
 * That is why beats-per-minute is `{beats}/min` rather than a bare `/min`: an
 * annotation is dimensionless and semantically empty, so it converts exactly as
 * `/min` does while staying distinguishable from breaths and from a plain rate.
 * LOINC spells heart rate the same way.
 *
 * NAMES come from UCUM via the NLM terminology service rather than being
 * written here, with two edits for a dropdown: its disambiguating suffixes are
 * parenthesised ("second - time" → "second (time)") and its plurals made
 * singular. The annotations and `1` have no UCUM name and are named here.
 *
 * ADDING ONE is a line here. It has to be a real UCUM expression — non-metric
 * atoms bracketed (`[in_i]`, `[lb_av]`, `[degF]`), and anything that is a score
 * rather than a unit written as an annotation (`{percentile}`), which carries no
 * dimension and therefore never converts or range-checks against anything.
 */
export const UNIT_CATALOG: readonly CatalogUnit[] = [
  // Dimensionless
  { code: "%", display: "%", name: "percent" },
  { code: "1", display: "ratio", name: "unity (dimensionless)" },
  // Length
  { code: "mm", display: "mm", name: "millimeter" },
  { code: "cm", display: "cm", name: "centimeter" },
  { code: "m", display: "m", name: "meter" },
  { code: "[in_i]", display: "in", name: "inch" },
  { code: "[ft_i]", display: "ft", name: "foot" },
  // Mass
  { code: "g", display: "g", name: "gram" },
  { code: "kg", display: "kg", name: "kilogram" },
  { code: "[lb_av]", display: "lb", name: "pound" },
  // Time
  { code: "ms", display: "ms", name: "millisecond" },
  { code: "s", display: "s", name: "second (time)" },
  { code: "min", display: "min", name: "minute" },
  { code: "h", display: "h", name: "hour" },
  { code: "d", display: "d", name: "day" },
  // Angle and angular velocity
  { code: "deg", display: "deg", name: "degree (plane angle)" },
  { code: "deg/s", display: "deg/s", name: "degree per second" },
  // Velocity
  { code: "cm/s", display: "cm/s", name: "centimeter per second" },
  { code: "m/s", display: "m/s", name: "meter per second" },
  { code: "[mi_i]/h", display: "mph", name: "mile per hour" },
  // Pressure
  { code: "mm[Hg]", display: "mmHg", name: "millimeter of mercury" },
  { code: "cm[H2O]", display: "cmH2O", name: "centimeter of water" },
  // Temperature
  { code: "Cel", display: "°C", name: "degree Celsius" },
  { code: "[degF]", display: "°F", name: "degree Fahrenheit" },
  // Frequency and rates
  { code: "Hz", display: "Hz", name: "hertz" },
  { code: "/s", display: "per second", name: "per second" },
  { code: "/min", display: "per minute", name: "per minute" },
  { code: "{beats}/min", display: "bpm", name: "beats per minute" },
  { code: "{breaths}/min", display: "breaths/min", name: "breaths per minute" },
  // Scores, which are not units at all
  { code: "{percentile}", display: "%ile", name: "percentile (a score, not a unit)" },
  { code: "{line_diff}", display: "line diff", name: "line difference (a score, not a unit)" },
];

const BY_CODE = new Map(UNIT_CATALOG.map((unit) => [unit.code, unit]));

/** The catalogued unit for a code, or undefined where nothing declares it. */
export function unitByCode(code: string): CatalogUnit | undefined {
  return BY_CODE.get(code);
}

/**
 * A stored code as the pair a value holds — never the name, which is the
 * picker's business. A code the catalog no longer lists still reads: it stands
 * in as its own display rather than vanishing, because a unit that disappeared
 * from a saved definition is worse than one that reads oddly.
 */
export function unitForCode(code: string): UnitValue {
  const found = BY_CODE.get(code);
  return found ? { code: found.code, display: found.display } : { code, display: code };
}
